import posthog from "posthog-js";
import { axiosBasic, getUserInfo } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import { actionTypes } from "./timeOffActionTypes";
import { Common } from "../../../utils";
import {
  getSelectedOffice,
  getEmployeeId,
  clearLocalStorageItem,
} from "../../Global/CustomFunctions";
import { LocalStorage } from "../../Global";
const { TimeOff, UserFavorites } = config.REST_API;

export const setEmpLeaves = (data, leaveTypesCount, annualLeaveas, leaveAllowanceSummary, annualTrends, timeOffs, pieChartData, noDeductibleLeaveTypesInfo = null) => {
  return {
    type: actionTypes.SET_EMP_LEAVES,
    payload: {
      leaves: data,
      leaveTypesCount: leaveTypesCount,
      annualLeaveas: annualLeaveas,
      leaveAllowanceSummary: leaveAllowanceSummary,
      annualTrends: annualTrends,
      timeOffs: timeOffs,
      pieChartData: pieChartData,
      noDeductibleLeaveTypesInfo: noDeductibleLeaveTypesInfo ?? null,
    },
  };
};

export const showLoading = (data) => {
  return { type: actionTypes.LOADING, data };
};
export const clearMonthlyLeaves = () => {
  return { type: actionTypes.CLEAR_MONTHLY_EMP_LEAVES };
};

/**
 * Merges leave updates from API (e.g. after approval/cancel) into wallchart employeeLeaves.
 * @param {Array} leaves - Array of leave objects from response.data.data.leave (each may have userId/UserId, id, status, etc.)
 */
export const mergeLeaveUpdates = (leaves) => {
  return {
    type: actionTypes.MERGE_LEAVE_UPDATES,
    payload: Array.isArray(leaves) ? leaves : [],
  };
};

/**
 * Applies employeeCalendar from API (same shape as GetEmployeeCalendar response) to the wall chart.
 * Used when updateLeaveStatus returns employeeCalendar (e.g. after cancel from time-off modal).
 * @param {Object} employeeCalendar - { data: Array, pagination?: Object } same as GetEmployeeCalendar response
 */
export const setEmployeeCalendarFromResponse = (employeeCalendar) => {
  return (dispatch) => {
    if (!employeeCalendar) return;
    const data = employeeCalendar.data ?? employeeCalendar;
    const pagination = employeeCalendar.pagination;
    if (Array.isArray(data)) {
      dispatch({ type: actionTypes.GET_EMP_MONTH_LEAVES, payload: data });
    }
    if (pagination) {
      dispatch({ type: actionTypes.SET_EMP_LEAVES_PAGINATION, payload: pagination });
    }
  };
};

export const clearTimeOffModal = () => {
  return { type: actionTypes.CLEAR_TIME_OFF_MODAL };
};
export const hideLoading = (data) => {
  return { type: actionTypes.LOADING, data };
};
export const clearYearlyLeaves = () => {
  return { type: actionTypes.CLEAR_YEARLY_EMP_LEAVES };
};
export const setEmpYearLeavesLoading = (payload) => {
  return { type: actionTypes.EMP_YEAR_LEAVES_LOADING, payload };
};
export const setLeaveTypes = (leaveTypes) => {
  return {
    type: actionTypes.FETCH_LEAVE_TYPES,
    payload: leaveTypes,
  };
};
export const getEmpYearLeaves = (id, fn, link, redirect, year = null) => {
  return (dispatch) => {
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = { id, year, redirect: !!redirect };
    dispatch(setEmpYearLeavesLoading(true));
    if (redirect) dispatch(showLoading(true));

    // Build the URL with year parameter if provided
    let url = TimeOff.GetEmpYearLeaves + id;
    if (year !== null) {
      url += `&year=${year}`;
    }

    axiosBasic
      .get(url)
      .then((response) => {
        const { success, data: responseData, message } = response.data || {};
        const hasData = success && responseData != null;

        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("getEmpYearLeaves", {
            type: hasData ? "info" : "exception",
            userId,
            success: hasData,
            userInput,
            reason: hasData ? undefined : (message || "No data"),
          });
        }

        if (!hasData) {
          Common.showErrorMessage(
            message || "Unable to load leave data. You may not have permission to view this calendar."
          );
          dispatch(
            setEmpLeaves(null, null, null, null, null, null, null, null)
          );
          dispatch(setEmpYearLeavesLoading(false));
          if (redirect) {
            dispatch(showLoading(false));
            fn(link);
          }
          return;
        }

        let data = responseData.empLeaves;
        let leaveTypesCount = responseData.leaveTypeCount;
        let annualLeaveas = responseData.annualLeaveas;
        let leaveAllowanceSummary = responseData.leaveAllowanceSummary;
        let annualTrends = responseData.annualTrends;
        let timeOffs = responseData.timeOffs;
        let pieChartData = responseData.pieChartData;
        let noDeductibleLeaveTypesInfo = responseData.noDeductibleLeaveTypesInfo ?? null;
        dispatch(setEmpLeaves(data, leaveTypesCount, annualLeaveas, leaveAllowanceSummary, annualTrends, timeOffs, pieChartData, noDeductibleLeaveTypesInfo));

        if (redirect) {
          dispatch(showLoading(false));
          fn(link);
        }
      })
      .catch((error) => {
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("getEmpYearLeaves", {
            type: "exception",
            userId,
            success: false,
            userInput,
            reason: error?.response?.data?.message || error?.message || "Request failed",
          });
        }
        dispatch(setEmpYearLeavesLoading(false));
        Common.showErrorMessage(error);
      });
  };
};
export const getEmployeeLeaves = (
  date,
  page = 1,
  pageSize = 50,
  loadMore = false,
  departmentId = null,
  departmentIds = null,
  accountType = null,
  sortBy = null
) => {
  let url = TimeOff.GetEmployeeCalendar;
  let office = getSelectedOffice();
  return (dispatch) => {
    if (!office || !office.id) {
      dispatch(showLoading(false));
      Common.showErrorMessage("Office not selected. Please select an office first.");
      return Promise.resolve();
    }

    if (!loadMore) {
      dispatch(showLoading(true));
    }

    const actualAccountType = accountType !== null && accountType !== undefined ? accountType : "all";
    const actualSortBy = sortBy !== null && sortBy !== undefined ? sortBy : "name";

    const sortByMap = {
      "firstname": "firstname",
      "lastname": "lastname",
      "department": "department",
      "name": "name"
    };
    const mappedSortBy = sortByMap[actualSortBy?.toLowerCase()] || "name";

    const accountTypeMap = {
      "all": "all",
      "departmentmanagers": "managers",
      "usersiapprove": "usersiapprove",
      "myfavourites": "myfavourites"
    };
    const lowerAccountType = actualAccountType?.toLowerCase();
    const mappedAccountType = accountTypeMap[lowerAccountType] || "all";

    let formattedDate = date;
    if (date instanceof Date) {
      formattedDate = date.toISOString();
    } else if (typeof date === 'string') {
      formattedDate = date;
    }

    const params = {
      officeId: office.id,
      date: formattedDate,
      page: page,
      pageSize: pageSize,
      accountType: mappedAccountType,
      sortBy: mappedSortBy
    };

    if (departmentIds && Array.isArray(departmentIds) && departmentIds.length > 0) {
      const validDepartmentIds = departmentIds
        .filter(id => id !== "all" && id !== null && id !== undefined)
        .map(id => parseInt(id))
        .filter(id => !isNaN(id));

      if (validDepartmentIds.length > 0) {
        params.departmentIds = validDepartmentIds;
      }
    } else if (departmentId !== null && departmentId !== undefined && departmentId !== "all") {
      const parsedDeptId = parseInt(departmentId);
      if (!isNaN(parsedDeptId)) {
        params.departmentId = parsedDeptId;
      }
    }

    const paramsSerializer = (params) => {
      const searchParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        const value = params[key];
        if (Array.isArray(value)) {
          value.forEach(item => {
            searchParams.append(key, item);
          });
        } else if (value !== null && value !== undefined) {
          searchParams.append(key, value);
        }
      });
      return searchParams.toString();
    };

    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = { date: formattedDate, page, pageSize, accountType: mappedAccountType, sortBy: mappedSortBy, departmentId, departmentIds: departmentIds ?? undefined };

    return axiosBasic
      .get(url, {
        params: params,
        paramsSerializer: paramsSerializer
      })
      .then((response) => {
        const baseModel = response.data;

        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("getEmployeeLeaves", {
            type: baseModel.success ? "info" : "exception",
            userId,
            success: baseModel.success,
            userInput,
            reason: baseModel.success ? undefined : baseModel.message,
          });
        }

        if (baseModel.success) {
          if (response.data != null && baseModel.data) {
            const employeeData = baseModel.data.data || baseModel.data;
            const pagination = baseModel.data.pagination || null;

            if (loadMore) {
              dispatch({
                type: actionTypes.APPEND_EMP_MONTH_LEAVES,
                payload: employeeData,
              });
            } else {
              dispatch({
                type: actionTypes.GET_EMP_MONTH_LEAVES,
                payload: employeeData,
              });
            }

            if (pagination) {
              dispatch({
                type: actionTypes.SET_EMP_LEAVES_PAGINATION,
                payload: pagination,
              });
            }
          } else {
            dispatch({ type: actionTypes.GET_EMP_MONTH_LEAVES, payload: [] });
          }
          return {
            success: true,
            data: baseModel.data?.data ?? baseModel.data,
            pagination: baseModel.data?.pagination
          };
        } else {
          dispatch(showLoading(false));
          Common.showErrorMessage(baseModel.message);
          return { success: false, message: baseModel.message };
        }
      })
      .catch((error) => {
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("getEmployeeLeaves", {
            type: "exception",
            userId,
            success: false,
            userInput,
            reason: error?.response?.data?.message || error?.message || "Request failed",
          });
        }
        dispatch(showLoading(false));
        Common.showErrorMessage(error?.response?.data?.message || error?.message || "Failed to load employee leaves");
      });
  };
};

export const bookEmployeeTimeOff = (timeOffData, officeId) => {
  let id = getEmployeeId();
  let url = TimeOff.BookEmployeeTimeOff;

  const startDate = new Date(timeOffData.StartDate);
  const endDate = new Date(timeOffData.EndDate);
  const isMultiDay = startDate.toDateString() !== endDate.toDateString();

  let startingLeave = timeOffData.StartingLeave;
  let endingLeave = timeOffData.EndingLeave;


  const model = {
    UserId: timeOffData.EmployeeId,
    StartDate: timeOffData.StartDate,
    EndDate: timeOffData.EndDate,
    LeaveTypeId: timeOffData.LeaveTypeId,
    LeaveNote: timeOffData.LeaveNote,
    OfficeId: officeId,
    StartingLeave: startingLeave,
    EndingLeave: endingLeave,
    IsMultiDayRequest: isMultiDay,
    DaySequence: 1,
  };

  return (dispatch) => {
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = {
      EmployeeId: timeOffData.EmployeeId,
      StartDate: timeOffData.StartDate,
      EndDate: timeOffData.EndDate,
      LeaveTypeId: timeOffData.LeaveTypeId,
      LeaveNote: timeOffData.LeaveNote ? "(present)" : undefined,
      OfficeId: officeId,
      StartingLeave: startingLeave,
      EndingLeave: endingLeave,
    };
    return axiosBasic
      .post(url, model)
      .then((response) => {
        const success = response?.data?.success !== false;
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("bookEmployeeTimeOff", {
            type: success ? "info" : "exception",
            userId,
            success,
            userInput,
            reason: success ? undefined : response?.data?.message,
          });
        }
        return response.data;
      })
      .catch((error) => {
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("bookEmployeeTimeOff", {
            type: "exception",
            userId,
            success: false,
            userInput,
            reason: error?.response?.data?.message || error?.message || "Request failed",
          });
        }
        return error;
      });
  };
};

export const updateLeave = (timeOffData, officeId) => {
  let url = TimeOff.UpdateLeave;

  const startDate = new Date(timeOffData.StartDate);
  const endDate = new Date(timeOffData.EndDate);
  const isMultiDay = startDate.toDateString() !== endDate.toDateString();

  let startingLeave = timeOffData.StartingLeave;
  let endingLeave = timeOffData.EndingLeave;

  const model = {
    GroupId: timeOffData.GroupId,
    UserId: timeOffData.EmployeeId,
    StartDate: timeOffData.StartDate,
    EndDate: timeOffData.EndDate,
    LeaveTypeId: timeOffData.LeaveTypeId,
    LeaveNote: timeOffData.LeaveNote,
    OfficeId: officeId,
    StartingLeave: startingLeave,
    EndingLeave: endingLeave,
    IsMultiDayRequest: isMultiDay,
    DaySequence: 1,
  };

  return (dispatch) => {
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = {
      GroupId: timeOffData.GroupId,
      EmployeeId: timeOffData.EmployeeId,
      StartDate: timeOffData.StartDate,
      EndDate: timeOffData.EndDate,
      LeaveTypeId: timeOffData.LeaveTypeId,
      LeaveNote: timeOffData.LeaveNote ? "(present)" : undefined,
      OfficeId: officeId,
      StartingLeave: startingLeave,
      EndingLeave: endingLeave,
    };
    return axiosBasic
      .put(url, model)
      .then((response) => {
        const success = response?.data?.success !== false;
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("updateLeave", {
            type: success ? "info" : "exception",
            userId,
            success,
            userInput,
            reason: success ? undefined : response?.data?.message,
          });
        }
        return response.data;
      })
      .catch((error) => {
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("updateLeave", {
            type: "exception",
            userId,
            success: false,
            userInput,
            reason: error?.response?.data?.message || error?.message || "Request failed",
          });
        }
        return error;
      });
  };
};

export const getLeaveTypes = () => {
  let url = TimeOff.GetLeaveTypes;
  let office = getSelectedOffice();
  return (dispatch) => {
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = { officeId: office?.Id ?? office?.id };
    return axiosBasic
      .get(url, office.Id)
      .then((response) => {
        const baseModel = response.data;
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("getLeaveTypes", {
            type: baseModel.success ? "info" : "exception",
            userId,
            success: baseModel.success,
            userInput,
            reason: baseModel.success ? undefined : baseModel.message,
          });
        }
        if (baseModel.success) {
          if (response.data != null) {
            let leaveTypes = baseModel.data;
            localStorage.setItem(
              LocalStorage.leaveTypes,
              JSON.stringify({ leaveTypes })
            );
            dispatch(setLeaveTypes(leaveTypes));
          }
        } else {
          Common.showErrorMessage(baseModel.message);
        }
      })
      .catch((error) => {
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("getLeaveTypes", {
            type: "exception",
            userId,
            success: false,
            userInput,
            reason: error?.response?.data?.message || error?.message || "Request failed",
          });
        }
        Common.showErrorMessage(error);
      });
  };
};

export const getLeaveTypesByEmployee = (employeeId) => {
  let url = TimeOff.GetLeaveTypes + employeeId;
  return (dispatch) => {
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = { employeeId };
    return axiosBasic
      .get(url)
      .then((response) => {
        const baseModel = response.data;
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("getLeaveTypesByEmployee", {
            type: baseModel.success ? "info" : "exception",
            userId,
            success: baseModel.success,
            userInput,
            reason: baseModel.success ? undefined : baseModel.message,
          });
        }
        if (baseModel.success) {
          if (response.data != null) {
            let leaveTypes = baseModel.data;
            dispatch(setLeaveTypes(leaveTypes));
          }
        } else {
          Common.showErrorMessage(baseModel.message);
        }
      })
      .catch((error) => {
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("getLeaveTypesByEmployee", {
            type: "exception",
            userId,
            success: false,
            userInput,
            reason: error?.response?.data?.message || error?.message || "Request failed",
          });
        }
        Common.showErrorMessage(error);
      });
  };
};

export const setValidationLoading = (loading) => {
  return {
    type: actionTypes.SET_VALIDATION_LOADING,
    payload: loading,
  };
};

export const setValidationResult = (result) => {
  return {
    type: actionTypes.SET_VALIDATION_RESULT,
    payload: result,
  };
};

export const clearValidationResult = () => {
  return {
    type: actionTypes.CLEAR_VALIDATION_RESULT,
  };
};

export const validateLeaveAvailability = (validationData) => {
  return (dispatch) => {
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = {
      userId: validationData.userId,
      startDate: validationData.startDate,
      endDate: validationData.endDate,
      leaveTypeId: validationData.leaveTypeId,
      isEdit: validationData.isEdit || false,
      existingGroupId: validationData.existingGroupId || null,
    };
    dispatch(setValidationLoading(true));
    dispatch(clearValidationResult());

    const validationDto = {
      UserId: validationData.userId,
      StartDate: validationData.startDate,
      EndDate: validationData.endDate,
      StartingLeave: validationData.startingLeave,
      EndingLeave: validationData.endingLeave,
      LeaveTypeId: validationData.leaveTypeId,
      IsEdit: validationData.isEdit || false,
      ExistingGroupId: validationData.existingGroupId || null,
    };

    return axiosBasic
      .post(TimeOff.ValidateLeaveAvailability, validationDto)
      .then((response) => {
        const baseModel = response.data;
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("validateLeaveAvailability", {
            type: baseModel.success ? "info" : "exception",
            userId,
            success: baseModel.success,
            userInput,
            reason: baseModel.success ? undefined : baseModel.message,
          });
        }
        if (baseModel.success) {
          const validationResult = {
            isAvailable: baseModel.data.isAvailable,
            message: baseModel.data.message || baseModel.message,
            conflicts: baseModel.data.conflicts || [],
            remainingBalance: baseModel.data.remainingBalance || 0,
            leaveDivision: baseModel.data.leaveDivision || "Days",
            remainingUserLeavesInDays: baseModel.data.remainingUserLeavesInDays || 0,
            remainingUserLeavesInHours: baseModel.data.remainingUserLeavesInHours || 0,
            totalUserLeavesInDays: baseModel.data.totalUserLeavesInDays || 0,
            totalUserLeavesInHours: baseModel.data.totalUserLeavesInHours || 0,
            isValidating: false,
          };
          dispatch(setValidationResult(validationResult));
        } else {
          const validationResult = {
            isAvailable: false,
            message: baseModel.message || "Leave validation failed",
            remainingBalance: 0,
            leaveDivision: "Days",
            isValidating: false,
          };
          dispatch(setValidationResult(validationResult));
        }
      })
      .catch((error) => {
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("validateLeaveAvailability", {
            type: "exception",
            userId,
            success: false,
            userInput,
            reason: error?.response?.data?.message || error?.message || "Request failed",
          });
        }
        const validationResult = {
          isAvailable: false,
          message: error.message || "Error validating leave availability",
          remainingBalance: 0,
          leaveDivision: "Days",
          isValidating: false,
        };
        dispatch(setValidationResult(validationResult));
        Common.showErrorMessage(error);
      })
      .finally(() => {
        dispatch(setValidationLoading(false));
      });
  };
};

export const getLeaveByGroupId = (groupId, userIdParam = null) => {
  return (dispatch) => {
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = { groupId, userId: userIdParam };
    let url = `${TimeOff.GetLeaveByGroupId}${groupId}`;
    if (userIdParam) {
      url += `&userId=${userIdParam}`;
    }

    return axiosBasic
      .get(url)
      .then((response) => {
        const baseModel = response.data;
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("getLeaveByGroupId", {
            type: baseModel.success ? "info" : "exception",
            userId,
            success: baseModel.success,
            userInput,
            reason: baseModel.success ? undefined : baseModel.message,
          });
        }
        if (baseModel.success) {
          return baseModel.data;
        } else {
          throw new Error(baseModel.message || "Failed to get leave details");
        }
      })
      .catch((error) => {
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("getLeaveByGroupId", {
            type: "exception",
            userId,
            success: false,
            userInput,
            reason: error?.response?.data?.message || error?.message || "Request failed",
          });
        }
        Common.showErrorMessage(error);
        throw error;
      });
  };
};

export const upsertFavorite = (favoriteUserId) => {
  return (dispatch) => {
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = { favoriteUserId };
    return axiosBasic
      .post(UserFavorites.UpsertFavorite, favoriteUserId
      )
      .then((response) => {
        const baseModel = response.data;
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("upsertFavorite", {
            type: baseModel.success ? "info" : "exception",
            userId,
            success: baseModel.success,
            userInput,
            reason: baseModel.success ? undefined : baseModel.message,
          });
        }
        if (baseModel.success) {
          return baseModel;
        } else {
          Common.showErrorMessage(baseModel.message || "Failed to update favorite");
          return { success: false, message: baseModel.message };
        }
      })
      .catch((error) => {
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("upsertFavorite", {
            type: "exception",
            userId,
            success: false,
            userInput,
            reason: error?.response?.data?.message || error?.message || "Request failed",
          });
        }
        Common.showErrorMessage(error?.response?.data?.message || error?.message || "Failed to update favorite");
        return { success: false, message: error?.response?.data?.message || error?.message };
      });
  };
};

export const postBulkGroupLeaves = (bulkLeaveData) => {
  let url = TimeOff.PostBulkGroupLeaves;

  const startDate = new Date(bulkLeaveData.StartDate);
  const endDate = new Date(bulkLeaveData.EndDate);
  const isMultiDay = startDate.toDateString() !== endDate.toDateString();

  const model = {
    DepartmentId: bulkLeaveData.DepartmentId,
    LeaveTypeId: bulkLeaveData.LeaveTypeId,
    LeaveModel: {
      StartDate: bulkLeaveData.StartDate,
      EndDate: bulkLeaveData.EndDate,
      LeaveNote: bulkLeaveData.LeaveNote || "",
      StartingLeave: bulkLeaveData.StartingLeave || 0,
      EndingLeave: bulkLeaveData.EndingLeave || 0,
      IsMultiDayRequest: isMultiDay,
      DaySequence: 1,
    }
  };

  return (dispatch) => {
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = {
      DepartmentId: bulkLeaveData.DepartmentId,
      LeaveTypeId: bulkLeaveData.LeaveTypeId,
      StartDate: bulkLeaveData.StartDate,
      EndDate: bulkLeaveData.EndDate,
      LeaveNote: bulkLeaveData.LeaveNote ? "(present)" : undefined,
      StartingLeave: bulkLeaveData.StartingLeave,
      EndingLeave: bulkLeaveData.EndingLeave,
    };
    return axiosBasic
      .post(url, model)
      .then((response) => {
        const success = response?.data?.success !== false;
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("postBulkGroupLeaves", {
            type: success ? "info" : "exception",
            userId,
            success,
            userInput,
            reason: success ? undefined : response?.data?.message,
          });
        }
        return response.data;
      })
      .catch((error) => {
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("postBulkGroupLeaves", {
            type: "exception",
            userId,
            success: false,
            userInput,
            reason: error?.response?.data?.message || error?.message || "Request failed",
          });
        }
        return error;
      });
  };
};
